import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { FarmData } from 'types/FarmData';
import { FarmProducts as FarmProductsPage } from 'types/FarmProducts';
import PublicRoute from 'enums/publicRoutes';
import { useBannerState } from 'public/context';
import Banner from 'public/components/DefaultLayout/Banner';
import PageBreadcrumbs, {
  PageBreadcrumb,
} from 'public/components/DefaultLayout/PageBreadcrumbs';
import FarmLocationCard from 'public/components/DefaultLayout/FarmLocationCard';
import FarmProducts from 'public/components/DefaultLayout/FarmProducts';
import useMarketType from 'utils/useMarketType';
import { StyledFarmView } from './style';
import FarmHero from './components/FarmHero';
import FarmTabs from './components/FarmTabs';
import FarmPhotoStrip from './components/FarmPhotoStrip';

const FARM_PRODUCTS_SECTION_ID = 'farm-products';

export interface FarmViewProps {
  farm?: FarmData;
  farmProducts?: FarmProductsPage;
  onFarmProductsPageChange: (page: number) => void;
}

const FarmView = ({
  farm,
  farmProducts,
  onFarmProductsPageChange,
}: FarmViewProps) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { isEmarket } = useMarketType();
  const { banners } = useBannerState();

  const farmsPath = isEmarket
    ? PublicRoute.FARMS_ALL
    : PublicRoute.WHOLESALE_FARMS_ALL;

  const crumbs = useMemo<PageBreadcrumb[]>(
    () => [
      {
        id: 'farms',
        name: intl.formatMessage({ id: 'breadcrumbs.farms' }),
        path: farmsPath,
      },
    ],
    [intl, farmsPath]
  );

  const banner = banners.data.find((item) => item.type === 'banner_4');

  if (!farm?.id) return null;

  const farmName = farm.brand ?? farm.name;
  const hasProducts = !!farmProducts?.data?.length;

  const scrollToProducts = () =>
    document
      .getElementById(FARM_PRODUCTS_SECTION_ID)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <StyledFarmView className={`farm-page${!isEmarket ? ' wholesale' : ''}`}>
      <section className={'farm-intro'}>
        <div className={'container'}>
          <PageBreadcrumbs
            className={'farm-nav'}
            crumbs={crumbs}
            backTo={crumbs[0]}
            backLabel={
              <FormattedMessage
                id={'farm_view.back_to_farms'}
                values={{
                  b: (chunks) => (
                    <strong className={'page-back-target'}>{chunks}</strong>
                  ),
                }}
              />
            }
            onBack={() => navigate(farmsPath)}
          />
          <FarmHero
            farm={farm}
            onProductsClick={hasProducts ? scrollToProducts : undefined}
          />
        </div>
      </section>

      <section className={'farm-details'}>
        <div className={'container'}>
          <div className={'farm-details-layout'}>
            <FarmTabs farm={farm} isEmarket={isEmarket} />
            <FarmLocationCard
              farm={farm}
              infrastructures={farm.farm_infrastructures}
              isEmarket={isEmarket}
              showFarmPill={false}
            />
          </div>
        </div>
      </section>

      <FarmPhotoStrip posts={farm.social_posts} />

      <FarmProducts
        id={FARM_PRODUCTS_SECTION_ID}
        farmName={farmName}
        products={farmProducts?.data}
        total={farmProducts?.total}
        pageSize={farmProducts?.per_page}
        currentPage={farmProducts?.current_page}
        onPageChange={onFarmProductsPageChange}
      />

      {banner && (
        <div className={'banners'}>
          <div className={'container'}>
            <Banner banners={[banner]} />
          </div>
        </div>
      )}
    </StyledFarmView>
  );
};

export default FarmView;
